/**
 * 18 Oct 2026 (Eli: "one photo, three files"): a photo is sent to the visit's folder once — never twice.
 *
 * The Finish sheet sends each After photo as soon as it is taken, and Record sends whatever is still on the device.
 * Both go through one sender, which knows every photo (by its Blob) that is on its way or already in the folder:
 *   - a photo already in the folder answers with its path, nothing is sent;
 *   - a photo on its way answers with that same send (Record waits for it rather than sending it again);
 *   - a straightened photo is a new Blob, so it is sent as a new file beside the original (the app never deletes
 *     files) and the sheet then carries only the straightened one's path.
 * A send the server did not take is forgotten, so Try again (or Record) can send it again.
 */
export type SendPhoto = (blob: Blob, name: string) => Promise<string | null>;

export interface PhotoSender {
  /** The folder path of a photo the server already took, else undefined. */
  pathOf(blob: Blob): string | undefined;
  /** True while this photo is on its way. */
  isSending(blob: Blob): boolean;
  /** Sends the photo unless it is already in the folder or on its way; answers its path, or null when not taken. */
  send(blob: Blob, name: string): Promise<string | null>;
  /** Resolves when every send on its way has finished. */
  settled(): Promise<void>;
}

export function createPhotoSender(send: SendPhoto): PhotoSender {
  const done = new WeakMap<Blob, string>();
  const onWay = new Map<Blob, Promise<string | null>>();
  return {
    pathOf: (blob) => done.get(blob),
    isSending: (blob) => onWay.has(blob),
    send(blob, name) {
      const had = done.get(blob);
      if (had) return Promise.resolve(had);
      const running = onWay.get(blob);
      if (running) return running;
      const p = (async () => {
        try {
          const path = await send(blob, name);
          if (path) done.set(blob, path);
          return path;
        } catch {
          return null;
        } finally {
          onWay.delete(blob);
        }
      })();
      onWay.set(blob, p);
      return p;
    },
    async settled() {
      while (onWay.size) await Promise.all([...onWay.values()]);
    },
  };
}

/**
 * After photos taken off the sheet once they were already in the folder (removed, or replaced by their straightened
 * version): kept for this tab, so a phone that reloads the page does not bring them back onto the close-out.
 * The files stay in the folder (no deletes); only the report leaves them out.
 */
const offKey = (appointmentId: string) => `abf.finish.takenOff.${appointmentId}`;

export function takenOffPaths(appointmentId: string): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(offKey(appointmentId));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function rememberTakenOff(appointmentId: string, paths: string[]): void {
  if (!paths.length) return;
  try {
    const all = takenOffPaths(appointmentId);
    for (const p of paths) all.add(p);
    window.sessionStorage.setItem(offKey(appointmentId), JSON.stringify([...all]));
  } catch {
    /* a private window: the report may then list the original too — never in the way */
  }
}
